import React from "react";
import {
  Camera,
  TrendingUp,
  Activity,
  CheckCircle,
  Download,
  BarChart3,
  Smartphone,
  Scale,
  Globe,
  Star,
  Scan,
  Shield,
  Award,
} from "lucide-react";
import Typography from "./shared/Typography";
import AdSenseAd from "./AdSenseAd";
import { colors } from "../constants/colors";

const Homepage = () => {
  const [isMobile, setIsMobile] = React.useState(false);
  const [isFloatingVisible, setIsFloatingVisible] = React.useState(false);

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  React.useEffect(() => {
    const handleScroll = () => {
      setIsFloatingVisible(window.scrollY > 800);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const coreFeatures = [
    {
      icon: <Camera size={50} />,
      title: "AI-Powered Meal Analysis",
      subtitle: "Snap, Analyze, Track",
      description:
        "Take a photo of any meal and get instant nutritional breakdown with AI-powered image recognition. Our technology analyzes calories, macros, and micronutrients in seconds - no more guessing or manual entry!",
      image: "/Images/2.png",
      gradient: `linear-gradient(135deg, ${colors.darkBlue}, ${colors.lightBlue})`,
    },
    {
      icon: <BarChart3 size={50} />,
      title: "Personal Health Dashboard",
      subtitle: "Your Complete Health Picture",
      description:
        "See your wellness journey at a glance with daily nutrition tracking, weight progress monitoring, BMI calculations, and milestone celebrations. Everything you need to stay motivated, beautifully organized in one place.",
      image: "/Images/3.png",
      gradient: `linear-gradient(135deg, ${colors.darkGreen}, ${colors.lightGreen})`,
    },
    {
      icon: <TrendingUp size={50} />,
      title: "Advanced Analytics & Progress Tracking",
      subtitle: "Data That Motivates",
      description:
        "Visualize your health journey with detailed charts, weight trends, nutrition analytics, and progress milestones. Track your achievements over time and see how every healthy choice adds up to real results.",
      image: "/Images/4.PNG",
      gradient: `linear-gradient(135deg, ${colors.darkBlue}, ${colors.darkGreen})`,
    },
  ];

  const smartFeatures = [
    {
      icon: <Scale size={40} />,
      title: "Intelligent Weight Management",
      subtitle: "Progress, Not Perfection",
      description:
        "Monitor your weight journey with smart tracking, BMI calculations, and milestone recognition. Set realistic goals and celebrate every step forward on your wellness path.",
    },
    {
      icon: <Activity size={40} />,
      title: "Fitness Integration",
      subtitle: "Your Activity, Connected",
      description:
        "Seamlessly sync with Apple Health and Google Health Connect to automatically track steps, distance, calories burned, and daily activities. Your health data works together for better insights.",
    },
    {
      icon: <Smartphone size={40} />,
      title: "Flexible Meal Logging",
      subtitle: "Multiple Ways to Track",
      description:
        "Log meals with photos or describe them with text for accurate nutritional analysis. Perfect for homemade dishes, restaurant meals, or when photos aren't convenient.",
    },
    {
      icon: <Scan size={40} />,
      title: "Barcode Scanner",
      subtitle: "Instant Product Recognition",
      description:
        "Scan any barcode to instantly get detailed nutritional information from our comprehensive food database. Perfect for packaged foods and quick logging.",
    },
  ];

  const personalizationFeatures = [
    {
      icon: <Shield size={40} />,
      title: "Comprehensive Diet Support",
      subtitle: "Nutrition That Understands You",
      description:
        "Support for all dietary needs including allergies, intolerances, keto, vegan, diabetic, heart-healthy diets, and more. Every recommendation respects your unique health requirements.",
    },
    {
      icon: <Star size={40} />,
      title: "Favorite Meals & Quick Access",
      subtitle: "Your Go-To Meals, Instantly",
      description:
        "Save your frequently eaten meals for one-tap logging. Build your personal meal library with complete nutritional data for faster, more consistent tracking.",
    },
    {
      icon: <Globe size={40} />,
      title: "Global Support",
      subtitle: "Health Speaks Your Language",
      description:
        "Available in multiple languages with region-specific nutrition data and cultural food preferences. Healthy living, anywhere in the world.",
    },
    {
      icon: <Award size={40} />,
      title: "Premium Features",
      subtitle: "Unlock Your Full Potential",
      description:
        "Access advanced analytics, unlimited meal tracking, premium recipes, detailed micronutrient analysis, and enhanced goal-setting tools for the ultimate nutrition experience.",
    },
  ];

  // Enhanced Styles
  const containerStyle = {
    minHeight: "100vh",
    backgroundColor: colors.extraLightGray,
    position: "relative",
  };

  const heroSectionStyle = {
    background: `linear-gradient(135deg, ${colors.darkBlue} 0%, ${colors.darkGreen} 100%)`,
    color: colors.white,
    padding: isMobile ? "80px 20px" : "120px 20px",
    textAlign: "center",
    position: "relative",
    overflow: "hidden",
    minHeight: isMobile ? "90vh" : "100vh",
    display: "flex",
    alignItems: "center",
  };

  const heroContentStyle = {
    maxWidth: "1200px",
    margin: "0 auto",
    display: "grid",
    gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
    alignItems: "center",
    gap: isMobile ? "3rem" : "5rem",
    width: "100%",
  };

  const downloadButtonsStyle = {
    display: "flex",
    gap: isMobile ? "0.75rem" : "1.5rem",
    marginTop: "2.5rem",
    flexDirection: isMobile ? "column" : "row",
    alignItems: "center",
    justifyContent: isMobile ? "center" : "flex-start",
  };

  const appStoreButtonStyle = {
    backgroundColor: colors.white,
    color: colors.darkBlue,
    padding: isMobile ? "16px 32px" : "16px 32px",
    borderRadius: "16px",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    fontWeight: 700,
    fontSize: isMobile ? "1rem" : "1.1rem",
    textDecoration: "none",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    minWidth: isMobile ? "280px" : "220px",
    justifyContent: "center",
    boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
    transform: "translateY(0)",
  };

  const googlePlayButtonStyle = {
    backgroundColor: colors.lightGreen,
    color: colors.darkGreen,
    padding: isMobile ? "16px 32px" : "16px 32px",
    borderRadius: "16px",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    fontWeight: 700,
    fontSize: isMobile ? "1rem" : "1.1rem",
    textDecoration: "none",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    minWidth: isMobile ? "280px" : "220px",
    justifyContent: "center",
    boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
    transform: "translateY(0)",
  };

  const floatingCTAStyle = {
    position: "fixed",
    bottom: isMobile ? "20px" : "30px",
    right: isMobile ? "20px" : "30px",
    zIndex: 1000,
    display: "flex",
    flexDirection: isMobile ? "column" : "row",
    gap: "10px",
    transform: isFloatingVisible ? "translateY(0)" : "translateY(120px)",
    transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  };

  const floatingButtonStyle = {
    padding: isMobile ? "12px 20px" : "12px 24px",
    borderRadius: "50px",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontWeight: 600,
    fontSize: isMobile ? "0.9rem" : "1rem",
    textDecoration: "none",
    transition: "all 0.3s ease",
    boxShadow: "0 8px 25px rgba(0,0,0,0.3)",
    backdrop: "blur(10px)",
  };

  const sectionStyle = {
    padding: isMobile ? "60px 20px" : "100px 20px",
    maxWidth: "1400px",
    margin: "0 auto",
  };

  const featureCardStyle = {
    backgroundColor: colors.white,
    borderRadius: "24px",
    padding: isMobile ? "2rem" : "3rem",
    boxShadow: "0 15px 50px rgba(0,0,0,0.1)",
    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
    border: "1px solid rgba(255,255,255,0.2)",
    position: "relative",
    overflow: "hidden",
  };

  return (
    <div style={containerStyle}>
      {/* Floating CTA Buttons */}
      <div style={floatingCTAStyle}>
        <a
          href="https://apps.apple.com/se/app/nutriplate/id6749467809?l=en-GB"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            ...floatingButtonStyle,
            backgroundColor: colors.darkBlue,
            color: colors.white,
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = "scale(1.05)";
            e.currentTarget.style.boxShadow = "0 12px 35px rgba(0,0,0,0.4)";
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = "scale(1)";
            e.currentTarget.style.boxShadow = "0 8px 25px rgba(0,0,0,0.3)";
          }}
        >
          <Download size={18} />
          iOS
        </a>
        <a
          href="https://play.google.com/store/apps/details?id=com.peyman.lemolife&pcampaignid=web_share"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            ...floatingButtonStyle,
            backgroundColor: colors.darkGreen,
            color: colors.white,
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = "scale(1.05)";
            e.currentTarget.style.boxShadow = "0 12px 35px rgba(0,0,0,0.4)";
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = "scale(1)";
            e.currentTarget.style.boxShadow = "0 8px 25px rgba(0,0,0,0.3)";
          }}
        >
          <Download size={18} />
          Android
        </a>
      </div>

      {/* Enhanced Hero Section */}
      <section style={heroSectionStyle}>
        <div style={heroContentStyle}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: isMobile ? "center" : "flex-start",
              gap: "2rem",
              textAlign: isMobile ? "center" : "left",
            }}
          >
            <div>
              <Typography
                variant="h1"
                color={colors.white}
                style={{
                  fontSize: isMobile ? "2.5rem" : "4rem",
                  marginBottom: "1rem",
                  fontWeight: "800",
                  lineHeight: "1.1",
                  letterSpacing: "-0.02em",
                }}
              >
                Transform Your Health with{" "}
                <span
                  style={{
                    background: `linear-gradient(45deg, ${colors.lightGreen}, ${colors.lightBlue})`,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  AI-Powered
                </span>{" "}
                Nutrition
              </Typography>
              <Typography
                variant="subtitle"
                color={colors.white}
                style={{
                  opacity: 0.95,
                  fontSize: isMobile ? "1.1rem" : "1.3rem",
                  lineHeight: "1.6",
                  maxWidth: "500px",
                  margin: isMobile ? "0 auto" : "0",
                }}
              >
                Snap photos of your meals, get instant nutrition analysis, and
                achieve your health goals with personalized insights that adapt
                to your lifestyle.
              </Typography>
            </div>

            <div style={downloadButtonsStyle}>
              <a
                href="https://apps.apple.com/se/app/nutriplate/id6749467809?l=en-GB"
                target="_blank"
                rel="noopener noreferrer"
                style={appStoreButtonStyle}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform =
                    "translateY(-3px) scale(1.02)";
                  e.currentTarget.style.boxShadow =
                    "0 15px 40px rgba(0,0,0,0.3)";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = "translateY(0) scale(1)";
                  e.currentTarget.style.boxShadow =
                    "0 10px 30px rgba(0,0,0,0.2)";
                }}
              >
                <Download size={24} />
                <div>
                  <div style={{ fontSize: "0.8rem", opacity: 0.8 }}>
                    Download for
                  </div>
                  <div style={{ fontSize: "1.1rem", fontWeight: "800" }}>
                    iOS
                  </div>
                </div>
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.peyman.lemolife&pcampaignid=web_share"
                target="_blank"
                rel="noopener noreferrer"
                style={googlePlayButtonStyle}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform =
                    "translateY(-3px) scale(1.02)";
                  e.currentTarget.style.boxShadow =
                    "0 15px 40px rgba(0,0,0,0.3)";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = "translateY(0) scale(1)";
                  e.currentTarget.style.boxShadow =
                    "0 10px 30px rgba(0,0,0,0.2)";
                }}
              >
                <Download size={24} />
                <div>
                  <div style={{ fontSize: "0.8rem", opacity: 0.8 }}>
                    Get it on
                  </div>
                  <div style={{ fontSize: "1.1rem", fontWeight: "800" }}>
                    Android
                  </div>
                </div>
              </a>
            </div>
          </div>

          {/* Enhanced App Screenshot */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              position: "relative",
            }}
          >
            <div
              style={{
                position: "relative",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <img
                src="/Images/1.png"
                alt="NutriPlate App"
                style={{
                  width: "100%",
                  maxWidth: isMobile ? "280px" : "400px",
                  height: "auto",
                  borderRadius: "30px",
                  boxShadow: "0 40px 80px rgba(0,0,0,0.5)",
                  objectFit: "contain",
                  transform: "perspective(1000px) rotateY(-5deg) rotateX(2deg)",
                  transition: "all 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform =
                    "perspective(1000px) rotateY(0deg) rotateX(0deg) scale(1.05)";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform =
                    "perspective(1000px) rotateY(-5deg) rotateX(2deg)";
                }}
              />

              {/* Enhanced floating elements */}
              <div
                style={{
                  position: "absolute",
                  top: "-40px",
                  right: "-40px",
                  width: "100px",
                  height: "100px",
                  background: `linear-gradient(45deg, ${colors.lightGreen}, ${colors.lightBlue})`,
                  borderRadius: "50%",
                  opacity: 0.8,
                  animation: "float 4s ease-in-out infinite",
                  zIndex: -1,
                }}
              />
              <div
                style={{
                  position: "absolute",
                  bottom: "-30px",
                  left: "-30px",
                  width: "80px",
                  height: "80px",
                  background: `linear-gradient(45deg, ${colors.darkBlue}, ${colors.darkGreen})`,
                  borderRadius: "50%",
                  opacity: 0.6,
                  animation: "float 3s ease-in-out infinite reverse",
                  zIndex: -1,
                }}
              />
              <div
                style={{
                  position: "absolute",
                  top: "60%",
                  left: "-50px",
                  width: "30px",
                  height: "30px",
                  background: colors.white,
                  borderRadius: "50%",
                  opacity: 0.9,
                  animation: "float 2s ease-in-out infinite",
                  zIndex: -1,
                }}
              />
            </div>
          </div>
        </div>
      </section>
      {/* Core Features Section */}
      <section style={sectionStyle}>
        <div style={{ textAlign: "center", marginBottom: "4rem" }}>
          <Typography
            variant="h2"
            color={colors.darkBlue}
            style={{
              fontSize: isMobile ? "2.5rem" : "3rem",
              fontWeight: "800",
              marginBottom: "1rem",
            }}
          >
            🚀 Core Features
          </Typography>
          <Typography
            variant="subtitle"
            color={colors.darkGray}
            style={{
              fontSize: "1.2rem",
              maxWidth: "600px",
              margin: "0 auto",
              lineHeight: "1.6",
            }}
          >
            Everything you need to revolutionize your nutrition journey
          </Typography>
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: isMobile ? "3rem" : "4rem",
          }}
        >
          {coreFeatures.map((feature, index) => {
            const isReversed = index % 2 === 1;
            return (
              <div
                key={index}
                style={{
                  ...featureCardStyle,
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                  gap: isMobile ? "2rem" : "4rem",
                  alignItems: "center",
                  gridTemplateAreas: isMobile
                    ? "'content' 'image'"
                    : isReversed
                    ? "'image content'"
                    : "'content image'",
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = "translateY(-8px)";
                  e.currentTarget.style.boxShadow =
                    "0 25px 60px rgba(0,0,0,0.15)";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow =
                    "0 15px 50px rgba(0,0,0,0.1)";
                }}
              >
                <div
                  style={{
                    gridArea: "content",
                    display: "flex",
                    flexDirection: "column",
                    gap: "1.5rem",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "1rem",
                      marginBottom: "1rem",
                    }}
                  >
                    <div
                      style={{
                        background: feature.gradient,
                        color: colors.white,
                        padding: "20px",
                        borderRadius: "20px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {feature.icon}
                    </div>
                    <div>
                      <Typography
                        variant="body"
                        color={colors.darkGreen}
                        style={{
                          fontSize: "0.9rem",
                          fontWeight: "600",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        {feature.subtitle}
                      </Typography>
                      <Typography
                        variant="h3"
                        color={colors.darkBlue}
                        style={{
                          fontWeight: "700",
                          fontSize: isMobile ? "1.5rem" : "1.8rem",
                          marginTop: "0.25rem",
                        }}
                      >
                        {feature.title}
                      </Typography>
                    </div>
                  </div>
                  <Typography
                    variant="body"
                    color={colors.darkGray}
                    style={{
                      lineHeight: "1.7",
                      fontSize: "1.1rem",
                    }}
                  >
                    {feature.description}
                  </Typography>
                </div>

                <div
                  style={{
                    gridArea: "image",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{
                      position: "relative",
                      borderRadius: "24px",
                      overflow: "hidden",
                      boxShadow: "0 20px 50px rgba(0,0,0,0.2)",
                    }}
                  >
                    <img
                      src={feature.image}
                      alt={feature.title}
                      style={{
                        width: "100%",
                        maxWidth: "400px",
                        height: "300px",
                        objectFit: "cover",
                        transition: "transform 0.6s ease",
                        borderRadius: "24px",
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.transform = "scale(1.05)";
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.transform = "scale(1)";
                      }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Advertisement */}
      <div
        style={{
          backgroundColor: colors.extraLightGray,
          padding: isMobile ? "2rem 1rem" : "3rem 2rem",
          textAlign: "center",
        }}
      >
        <AdSenseAd
          adSlot="1234567890"
          style={{
            margin: "0 auto",
            maxWidth: "728px",
          }}
        />
      </div>

      {/* Recipe Discovery Section */}
      <section
        style={{
          ...sectionStyle,
          backgroundColor: colors.lightGreen,
          borderRadius: "0",
        }}
      >
        <div style={{ textAlign: "center", marginBottom: "4rem" }}>
          <Typography
            variant="h2"
            color={colors.darkGreen}
            style={{
              fontSize: isMobile ? "2.5rem" : "3rem",
              fontWeight: "800",
              marginBottom: "1rem",
            }}
          >
            🍽️ Personalized Recipe Discovery
          </Typography>
          <Typography
            variant="subtitle"
            color={colors.darkGray}
            style={{
              fontSize: "1.2rem",
              maxWidth: "600px",
              margin: "0 auto",
              lineHeight: "1.6",
            }}
          >
            Discover recipes tailored to your unique dietary needs and
            preferences
          </Typography>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
            gap: isMobile ? "2rem" : "4rem",
            alignItems: "center",
            maxWidth: "1200px",
            margin: "0 auto",
          }}
        >
          {/* Image Section - Left Side */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              order: isMobile ? 2 : 1,
            }}
          >
            <div
              style={{
                position: "relative",
                width: "100%",
                maxWidth: "500px",
                borderRadius: "24px",
                cursor: "pointer",
                transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                padding: "20px",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform =
                  "translateY(-8px) scale(1.02)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = "translateY(0) scale(1)";
              }}
            >
              <img
                src="/Images/8.PNG"
                alt="Personalized Recipe"
                style={{
                  width: "100%",
                  height: "400px",
                  objectFit: "contain",
                  transition: "transform 0.6s ease",
                  borderRadius: "16px",
                  filter: "drop-shadow(0 20px 40px rgba(0,0,0,0.15))",
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = "scale(1.05)";
                  e.currentTarget.style.filter =
                    "drop-shadow(0 25px 50px rgba(0,0,0,0.2))";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = "scale(1)";
                  e.currentTarget.style.filter =
                    "drop-shadow(0 20px 40px rgba(0,0,0,0.15))";
                }}
              />
            </div>
          </div>

          {/* Text Section - Right Side */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "2rem",
              textAlign: isMobile ? "center" : "left",
              order: isMobile ? 1 : 2,
            }}
          >
            <div>
              <Typography
                variant="h3"
                color={colors.darkBlue}
                style={{
                  fontSize: isMobile ? "1.8rem" : "2.2rem",
                  fontWeight: "700",
                  marginBottom: "1rem",
                  lineHeight: "1.2",
                }}
              >
                Smart Recipe Recommendations
              </Typography>
              <Typography
                variant="body"
                color={colors.darkGray}
                style={{
                  fontSize: "1.1rem",
                  lineHeight: "1.7",
                  marginBottom: "1.5rem",
                }}
              >
                Get personalized recipe suggestions that match your dietary
                preferences, health goals, and available ingredients. Our
                intelligent system learns from your choices to provide better
                recommendations over time.
              </Typography>
            </div>

            <div
              style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                }}
              >
                <CheckCircle size={20} color={colors.darkGreen} />
                <Typography variant="body" color={colors.darkGray}>
                  Dietary restriction friendly (keto, vegan, gluten-free, etc.)
                </Typography>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                }}
              >
                <CheckCircle size={20} color={colors.darkGreen} />
                <Typography variant="body" color={colors.darkGray}>
                  Nutritionally balanced meal suggestions
                </Typography>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                }}
              >
                <CheckCircle size={20} color={colors.darkGreen} />
                <Typography variant="body" color={colors.darkGray}>
                  Ingredient-based recipe matching
                </Typography>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                }}
              >
                <CheckCircle size={20} color={colors.darkGreen} />
                <Typography variant="body" color={colors.darkGray}>
                  Step-by-step cooking instructions
                </Typography>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Smart Features Section */}
      <section style={sectionStyle}>
        <div style={{ textAlign: "center", marginBottom: "4rem" }}>
          <Typography
            variant="h2"
            color={colors.darkBlue}
            style={{
              fontSize: isMobile ? "2.5rem" : "3rem",
              fontWeight: "800",
              marginBottom: "1rem",
            }}
          >
            🎯 Smart Features
          </Typography>
          <Typography
            variant="subtitle"
            color={colors.darkGray}
            style={{
              fontSize: "1.2rem",
              maxWidth: "600px",
              margin: "0 auto",
              lineHeight: "1.6",
            }}
          >
            Intelligent tools that make healthy living effortless
          </Typography>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
            gap: "2rem",
          }}
        >
          {smartFeatures.map((feature, index) => (
            <div
              key={index}
              style={{
                ...featureCardStyle,
                textAlign: isMobile ? "center" : "left",
                display: "flex",
                flexDirection: "column",
                gap: "1.5rem",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = "translateY(-5px)";
                e.currentTarget.style.boxShadow =
                  "0 20px 60px rgba(0,0,0,0.15)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 15px 50px rgba(0,0,0,0.1)";
              }}
            >
              {feature.image && (
                <div
                  style={{
                    width: "100%",
                    height: "200px",
                    borderRadius: "16px",
                    overflow: "hidden",
                    marginBottom: "1rem",
                  }}
                >
                  <img
                    src={feature.image}
                    alt={feature.title}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      transition: "transform 0.4s ease",
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.transform = "scale(1.05)";
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = "scale(1)";
                    }}
                  />
                </div>
              )}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "1rem",
                  marginBottom: "1.5rem",
                  justifyContent: isMobile ? "center" : "flex-start",
                }}
              >
                <div
                  style={{
                    color: colors.darkGreen,
                    backgroundColor: `${colors.lightGreen}30`,
                    padding: "16px",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {feature.icon}
                </div>
                <div>
                  <Typography
                    variant="body"
                    color={colors.darkGreen}
                    style={{
                      fontSize: "0.9rem",
                      fontWeight: "600",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    {feature.subtitle}
                  </Typography>
                  <Typography
                    variant="h3"
                    color={colors.darkBlue}
                    style={{
                      fontWeight: "700",
                      fontSize: "1.3rem",
                      marginTop: "0.25rem",
                    }}
                  >
                    {feature.title}
                  </Typography>
                </div>
              </div>
              <Typography
                variant="body"
                color={colors.darkGray}
                style={{
                  lineHeight: "1.6",
                  fontSize: "1rem",
                }}
              >
                {feature.description}
              </Typography>
            </div>
          ))}
        </div>
      </section>

      {/* Personalization & Health Section */}
      <section
        style={{
          ...sectionStyle,
          backgroundColor: colors.lightBlue,
        }}
      >
        <div style={{ textAlign: "center", marginBottom: "4rem" }}>
          <Typography
            variant="h2"
            color={colors.darkBlue}
            style={{
              fontSize: isMobile ? "2.5rem" : "3rem",
              fontWeight: "800",
              marginBottom: "1rem",
            }}
          >
            🛡️ Personalization & Health
          </Typography>
          <Typography
            variant="subtitle"
            color={colors.darkGray}
            style={{
              fontSize: "1.2rem",
              maxWidth: "600px",
              margin: "0 auto",
              lineHeight: "1.6",
            }}
          >
            Nutrition that understands and adapts to your unique needs
          </Typography>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
            gap: "2rem",
          }}
        >
          {personalizationFeatures.map((feature, index) => (
            <div
              key={index}
              style={{
                ...featureCardStyle,
                textAlign: isMobile ? "center" : "left",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = "translateY(-5px)";
                e.currentTarget.style.boxShadow =
                  "0 20px 60px rgba(0,0,0,0.15)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 15px 50px rgba(0,0,0,0.1)";
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "1rem",
                  marginBottom: "1.5rem",
                  justifyContent: isMobile ? "center" : "flex-start",
                }}
              >
                <div
                  style={{
                    color: colors.darkBlue,
                    backgroundColor: `${colors.lightBlue}50`,
                    padding: "16px",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {feature.icon}
                </div>
                <div>
                  <Typography
                    variant="body"
                    color={colors.darkBlue}
                    style={{
                      fontSize: "0.9rem",
                      fontWeight: "600",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    {feature.subtitle}
                  </Typography>
                  <Typography
                    variant="h3"
                    color={colors.darkBlue}
                    style={{
                      fontWeight: "700",
                      fontSize: "1.3rem",
                      marginTop: "0.25rem",
                    }}
                  >
                    {feature.title}
                  </Typography>
                </div>
              </div>
              <Typography
                variant="body"
                color={colors.darkGray}
                style={{
                  lineHeight: "1.6",
                  fontSize: "1rem",
                }}
              >
                {feature.description}
              </Typography>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA Section */}
      <section
        style={{
          background: `linear-gradient(135deg, ${colors.darkBlue} 0%, ${colors.darkGreen} 100%)`,
          color: colors.white,
          padding: isMobile ? "80px 20px" : "120px 20px",
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            maxWidth: "800px",
            margin: "0 auto",
            position: "relative",
            zIndex: 1,
          }}
        >
          <Typography
            variant="h2"
            color={colors.white}
            style={{
              fontSize: isMobile ? "2.5rem" : "3.5rem",
              fontWeight: "800",
              marginBottom: "1.5rem",
              lineHeight: "1.1",
            }}
          >
            Ready to Transform Your Health?
          </Typography>
          <Typography
            variant="subtitle"
            color={colors.white}
            style={{
              marginBottom: "3rem",
              opacity: 0.95,
              fontSize: "1.3rem",
              lineHeight: "1.6",
              maxWidth: "600px",
              margin: "0 auto 3rem auto",
            }}
          >
            Join thousands of users who have revolutionized their nutrition with
            AI-powered tracking and personalized insights.
          </Typography>

          <div
            style={{
              display: "flex",
              gap: isMobile ? "1rem" : "2rem",
              flexDirection: isMobile ? "column" : "row",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: "2rem",
            }}
          >
            <a
              href="https://apps.apple.com/se/app/nutriplate/id6749467809?l=en-GB"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                ...appStoreButtonStyle,
                fontSize: "1.2rem",
                padding: "20px 40px",
                minWidth: isMobile ? "300px" : "250px",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform =
                  "translateY(-3px) scale(1.02)";
                e.currentTarget.style.boxShadow = "0 20px 50px rgba(0,0,0,0.4)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = "translateY(0) scale(1)";
                e.currentTarget.style.boxShadow = "0 10px 30px rgba(0,0,0,0.2)";
              }}
            >
              <Download size={28} />
              <div>
                <div style={{ fontSize: "0.9rem", opacity: 0.8 }}>
                  Download for
                </div>
                <div style={{ fontSize: "1.3rem", fontWeight: "800" }}>iOS</div>
              </div>
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=com.peyman.lemolife&pcampaignid=web_share"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                ...googlePlayButtonStyle,
                fontSize: "1.2rem",
                padding: "20px 40px",
                minWidth: isMobile ? "300px" : "250px",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform =
                  "translateY(-3px) scale(1.02)";
                e.currentTarget.style.boxShadow = "0 20px 50px rgba(0,0,0,0.4)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = "translateY(0) scale(1)";
                e.currentTarget.style.boxShadow = "0 10px 30px rgba(0,0,0,0.2)";
              }}
            >
              <Download size={28} />
              <div>
                <div style={{ fontSize: "0.9rem", opacity: 0.8 }}>
                  Get it on
                </div>
                <div style={{ fontSize: "1.3rem", fontWeight: "800" }}>
                  Android
                </div>
              </div>
            </a>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "2rem",
              flexWrap: "wrap",
              justifyContent: "center",
              marginTop: "2rem",
            }}
          >
            <div
              style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
            >
              <CheckCircle size={20} color={colors.lightGreen} />
              <Typography
                variant="body"
                color={colors.white}
                style={{ opacity: 0.9 }}
              >
                Free to download
              </Typography>
            </div>
            <div
              style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
            >
              <CheckCircle size={20} color={colors.lightGreen} />
              <Typography
                variant="body"
                color={colors.white}
                style={{ opacity: 0.9 }}
              >
                No credit card required
              </Typography>
            </div>
            <div
              style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
            >
              <CheckCircle size={20} color={colors.lightGreen} />
              <Typography
                variant="body"
                color={colors.white}
                style={{ opacity: 0.9 }}
              >
                Start tracking instantly
              </Typography>
            </div>
          </div>
        </div>

        {/* Background decorations */}
        <div
          style={{
            position: "absolute",
            top: "-100px",
            right: "-100px",
            width: "300px",
            height: "300px",
            background: `linear-gradient(45deg, ${colors.lightGreen}20, ${colors.lightBlue}20)`,
            borderRadius: "50%",
            zIndex: 0,
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: "-150px",
            left: "-150px",
            width: "400px",
            height: "400px",
            background: `linear-gradient(45deg, ${colors.lightBlue}15, ${colors.lightGreen}15)`,
            borderRadius: "50%",
            zIndex: 0,
          }}
        />
      </section>

      {/* Company Footer */}
      <footer
        style={{
          backgroundColor: colors.darkBlue,
          color: colors.white,
          padding: "3rem 2rem 2rem",
          textAlign: "center",
          borderTop: `3px solid ${colors.lightGreen}`,
        }}
      >
        <div
          style={{
            maxWidth: "1200px",
            margin: "0 auto",
          }}
        >
          <Typography
            variant="h3"
            style={{
              marginBottom: "1rem",
              color: colors.lightGreen,
            }}
          >
            NutriPlate
          </Typography>

          <Typography
            variant="body"
            style={{
              marginBottom: "2rem",
              opacity: 0.9,
              lineHeight: "1.6",
            }}
          >
            AI-powered nutrition tracking for a healthier lifestyle
          </Typography>

          {/* Company Information */}
          <div
            style={{
              backgroundColor: colors.darkGreen,
              padding: "1.5rem",
              borderRadius: "12px",
              marginBottom: "2rem",
              border: `2px solid ${colors.lightGreen}`,
            }}
          >
            <Typography
              variant="body"
              style={{
                color: colors.white,
                fontWeight: "600",
                marginBottom: "0.5rem",
              }}
            >
              Developed by RayaTech AB
            </Typography>
            <Typography
              variant="body"
              style={{
                color: colors.white,
                opacity: 0.9,
                fontSize: "0.9rem",
              }}
            >
              Organization Number: 559546-6904
            </Typography>
            <Typography
              variant="body"
              style={{
                color: colors.white,
                opacity: 0.9,
                fontSize: "0.9rem",
              }}
            >
              Stockholm, Sweden
            </Typography>
          </div>

          <Typography
            variant="body"
            style={{
              opacity: 0.7,
              fontSize: "0.9rem",
            }}
          >
            © 2024 RayaTech AB. All rights reserved.
          </Typography>
        </div>
      </footer>
    </div>
  );
};

export default Homepage;
